<script setup lang="ts">
import { onMounted, ref, h, nextTick } from "vue";
import { commonEmailValidator, formatTime } from "@zyzgroup/core-common";
import {
  TWEEN,
  type EasingFunction,
  Color,
  getCSSVar
} from "@zyzgroup/core-web";
import {
  getCEComponent,
  popTel,
  popBottom,
  popTop,
  popLeft,
  popRight,
  popCenter,
  popAbsolute,
  toast,
  loading,
  dialog,
  numberKeyboard
} from "../globalTeleport";

import Mouse from "../components/base/Mouse.vue";

import PageVerticalScrollProgressBar from "../components/progress/PageVerticalScrollProgressBar.vue";
import FPSStats from "../components/fps/FPSStats.vue";
import Button from "../components/form/Button.vue";
import Accordion from "../components/form/Accordion.vue";
import Input from "../components/form/Input.vue";
import InputRange from "../components/form/InputRange.vue";
import InputRangeBox from "../components/form/InputRangeBox.vue";

import LayoutPage from "../components/layout/LayoutPage.vue";
import LayoutCenter from "../components/layout/LayoutCenter.vue";
import CardFlip from "../components/3d/CardFlip.vue";
import Cube from "../components/3d/Cube.vue";

import Popover from "../components/pop/Popover.vue";
import ColorPicker from "../components/form/ColorPicker.vue";

import PlateDisplay from "../components/plate/PlateDisplay.vue";
import PlateMobile from "../components/plate/PlateMobile.vue";

import Image from "../components/image/Image.vue";
import imagesrc1 from "@root/assets/images/picture.jpg";
import imagesrc2 from "@root/assets/images/logo.png";
import imagesrc3 from "@root/assets/images/ad.jpg";
import imagemarilyn from "@root/assets/images/marilyn.png";
import imagebike from "@root/assets/images/bike.png";
import ImageView from "../components/image/ImageView.vue";
const imageViewMultiRef = ref();
function openImageViewMulti() {
  imageViewMultiRef.value?.open();
}

import HandWritePanel from "../components/hand/HandWritePanel.vue";
const handRef = ref();
const downloadHandWrite = () => {
  handRef.value.download();
};

import EaseGroupGraphDisplay from "../components/animate/EaseGroupGraphDisplay.vue";
const easeName = ref<string>("");
const easeFunction = ref<EasingFunction>(TWEEN.Easing.Linear.None);
function selectedEaseFunction(
  selectedEaseName: string,
  selectedEaseFunction: EasingFunction
) {
  easeName.value = selectedEaseName;
  easeFunction.value = selectedEaseFunction;
}

import InterpolationGroupGraphDisplay from "../components/animate/InterpolationGroupGraphDisplay.vue";

import CSSTransition from "../components/base/CSSTransition.vue";
const effectsRef = ref();
const superRainbowRef = ref();
const superRainbowEnter = () => {
  superRainbowRef.value?.trigger();
};
const imageMount = () => {
  effectsRef.value?.reset();
};
import CSSAnimation, {
  SupportedCSSAnimation
} from "../components/base/CSSAnimation.vue";
const CSSAnimationRefs = [];
const animationEnter = (index) => {
  CSSAnimationRefs[index]?.trigger();
};
const animationLeave = (index) => {
  CSSAnimationRefs[index]?.triggerReverse();
};

import Page, { SupportedPageEffect } from "../components/base/Page.vue";
const pageEffect = ref<SupportedPageEffect>("static");
const changePageEffect = (effect: SupportedPageEffect) => {
  pageEffect.value = effect;
};
const pageRef = ref();
function openPage() {
  pageRef.value?.open(pageEffect.value);
}
function closePage() {
  pageRef.value?.close(pageEffect.value);
}

import Routes, { SupportedRouteEffect } from "../components/base/Routes.vue";
const routeEffect = ref<SupportedRouteEffect>("moveToLeft");
const changeRouteEffect = (effect: SupportedRouteEffect) => {
  routeEffect.value = effect;
};
const routesRef = ref();
let totals = 0;
const routeChangeStart = (
  fromPageIndex: number,
  toPageIndex: number,
  totalPages: number
) => {
  // console.log(
  //   `routeChangeStart: ${fromPageIndex} to ${toPageIndex}, total ${totalPages}`
  // );
};
const routeChangeEnd = (
  fromPageIndex: number,
  toPageIndex: number,
  totalPages: number
) => {
  // console.log(
  //   `routeChangeEnd: ${fromPageIndex} to ${toPageIndex}, total ${totalPages}`
  // );
  totals = totalPages;
};
function resetPage() {
  routesRef.value?.reset([
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 1",
      attributes: {
        style: {
          backgroundColor: Color.next(0)
        }
      }
    },
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 2",
      attributes: {
        style: {
          backgroundColor: Color.next()
        }
      }
    },
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 3",
      attributes: {
        style: {
          backgroundColor: Color.next()
        }
      }
    }
  ]);
}
function pushPage() {
  routesRef.value?.push({
    // component: PlateMobile,
    component: LayoutCenter,
    child: `Page ${totals + 1}`,
    attributes: {
      style: {
        backgroundColor: Color.next()
      }
    }
  });
}
function popPage() {
  routesRef.value?.pop();
}
function forwardPage() {
  routesRef.value?.forward();
}
function backPage() {
  routesRef.value?.back();
}
function gotoPage() {
  routesRef.value?.goto(0);
}

import Video from "../components/video/Video.vue";
import videosrc from "@root/server-client/assets/videos/sintel.webm";
const videoRef = ref();
const playVideo = () => {
  videoRef.value?.play();
};
const pauseVideo = () => {
  videoRef.value?.pause();
};

import MapTianditu from "../components/map/MapTianditu.vue";

const popBottomSample = () => {
  return popBottom(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popBottomFixedSample = () => {
  return popBottom(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    ),
    {
      fixed: 400, // 固定高度
      contentBgColor: "pink"
    }
  );
};
const popTopSample = () => {
  return popTop(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popLeftSample = () => {
  return popLeft(
    h(
      "div",
      {
        style: "height: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popRightSample = () => {
  return popRight(
    h(
      "div",
      {
        style: "height: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popCenterSample = () => {
  return popCenter(
    h(
      "div",
      {
        style:
          "width: 200px;height: 200px;padding: 30px;background-color:#ffffff;"
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popAbsoluteSample = () => {
  return popAbsolute(
    h(
      "div",
      {
        style:
          "width: 200px;height: 200px;padding: 30px;background-color:#ffffff;"
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    ),
    {
      popLeft: "50%",
      popTop: "50%"
    }
  );
};
const openDialogSample = () => {
  dialog({
    title: "标题",
    content:
      "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    showClose: true,
    cancelText: "取消",
    confirmText: "确认",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const dialogToastSample = () => {
  toast("toast");
};
const openDialogLoadingSample = () => {
  const close = loading("加载中...");
  setTimeout(() => {
    close();
  }, 2000);
};
const openDialogMiniSample = () => {
  dialog({
    title: "标题",
    content: "ttttttttt",
    cancelText: "取消",
    confirmText: "确认",
    stylePattern: "transparent",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogRedSample = () => {
  dialog({
    title: "标题",
    content: "redredredredredredredredredredredredredredred",
    cancelText: "",
    confirmText: "确认",
    stylePattern: "icon-warn-red",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogBlueSample = () => {
  dialog({
    title: "标题",
    content: "blueblueblueblueblueblueblueblueblueblueblueblueblueblueblue",
    cancelText: "取消",
    confirmText: "确认",
    stylePattern: "icon-warn-blue",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openNumberKeyboardSample = () => {
  numberKeyboard({
    min: 100.02,
    max: 200.345,
    onNumber: (n) => {
      console.log("numberKeyboard: " + n);
    },
    onError: (msg) => {
      toast(msg);
    }
  });
};

import Loading1 from "../components/loading/Loading1.vue";
import Loading2 from "../components/loading/Loading2.vue";
import Loading3 from "../components/loading/Loading3.vue";
import Loading4 from "../components/loading/Loading4.vue";
import Loading5 from "../components/loading/Loading5.vue";
import Loading6 from "../components/loading/Loading6.vue";
import Loading7 from "../components/loading/Loading7.vue";
import Loading8 from "../components/loading/Loading8.vue";
import Loading9 from "../components/loading/Loading9.vue";
import Loading10 from "../components/loading/Loading10.vue";
import Loading11 from "../components/loading/Loading11.vue";

import RingText from "../components/chat/RingText.vue";

import ClockFlipper from "../components/clock/ClockFlipper.vue";
const clockFlipper = ref();
const startClockFlipper = () => {
  clockFlipper.value?.start(formatTime(new Date()));
};
const pauseClockFlipper = () => {
  clockFlipper.value?.pause();
};

import BorderFocusText from "../components/css/BorderFocusText.vue";
const borderFocusTextRef = ref();
import Circle from "../components/css/Circle.vue";
const circleRef = ref();
const cssMouseEnter = (r) => {
  switch (r) {
    case "borderFocusTextRef":
      borderFocusTextRef.value?.start();
      break;
    case "circleRef":
      circleRef.value?.start();
      break;
  }
};
const cssMouseLeave = (r) => {
  switch (r) {
    case "borderFocusTextRef":
      borderFocusTextRef.value?.end();
      break;
    case "circleRef":
      circleRef.value?.end();
      break;
  }
};

import RingIndicator from "../components/chat/RingIndicator.vue";
const percent = ref(0);
const resetRingIndicator = () => {
  percent.value = 0;
};
const addRingIndicator = () => {
  percent.value += Math.random() * 30;
};

let timestamp = ref("1695000491000");
setTimeout(() => {
  timestamp.value = new Date().getTime().toFixed(0);
  // console.log(timestamp.value)
}, 3000);

import SVGPrompt from "../components/svg/SVGPrompt.vue";
import SVGQuestion from "../components/svg/SVGQuestion.vue";
import SVGFail from "../components/svg/SVGFail.vue";
import SVGSuccess from "../components/svg/SVGSuccess.vue";
import SVGWaiting from "../components/svg/SVGWaiting.vue";
import SVGLock from "../components/svg/SVGLock.vue";
import SVGDown from "../components/svg/SVGDown.vue";
import SVGWarn from "../components/svg/SVGWarn.vue";
import SVGAdd from "../components/svg/SVGAdd.vue";
import SVGDelete from "../components/svg/SVGDelete.vue";
const blue = getCSSVar("--xg-blue");
import Icon from "../components/form/Icon.vue";
const icon = ref("carbon:accessibility");
const color = ref(blue);
const count = ref(0);
const iconClick = () => {
  icon.value =
    count.value++ % 2 == 0 ? "icon-park:add-subtract" : "carbon:accessibility";
};

import RadioGroup from "../components/form/RadioGroup.vue";
import CheckboxGroup from "../components/form/CheckboxGroup.vue";
import Switch from "../components/form/Switch.vue";
import Select from "../components/form/Select.vue";
import SelectAbsolute from "../components/form/SelectAbsolute.vue";
import SlideLeftItem from "../components/form/SlideLeftItem.vue";
import Status from "../components/form/Status.vue";

onMounted(() => {});
</script>

<template>
  <div style="position: relative; overflow: hidden">
    <div
      style="
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        width: 100%;
        height: 6px;
      "
    >
      <PageVerticalScrollProgressBar></PageVerticalScrollProgressBar>
    </div>

    <FPSStats></FPSStats>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Button</template>
      <template #content>
        <div>
          <Button
            :icon="icon"
            @throttleClick="iconClick"
          >
            <div>fullfill</div>
          </Button>
          <Button
            stylePattern="transparent"
            @throttleClick="iconClick"
          >
            <div>transparent</div>
          </Button>
          <Button
            width="100%"
            @throttleClick="iconClick"
          >
            <div>100%</div>
          </Button>
          <Button
            label="Disabled"
            :icon="icon"
            @click="iconClick"
            :disabled="true"
          ></Button>
          <Button
            label="outline"
            stylePattern="outline"
            borderRadius="22px"
            bgColor="#ffffff"
            :icon="icon"
            @click="iconClick"
          ></Button>
          <Button
            label="Disabled"
            stylePattern="outline"
            borderRadius="22px"
            bgColor="#ffffff"
            :icon="icon"
            @click="iconClick"
            :disabled="true"
          ></Button>
          <Button
            label="gradient"
            bgColorStart="#fa503c"
            bgColorEnd="#fa8c78"
          ></Button>
          <Button
            label="gradient"
            bgColorStart="#fa503c"
            bgColorEnd="#fa8c78"
            :disabled="true"
          ></Button>
          <Button
            label="gradient"
            bgColorStart="#5a82f0"
            bgColorEnd="#66b3ff"
          ></Button>
          <Button
            label="gradient"
            bgColorStart="#5a82f0"
            bgColorEnd="#66b3ff"
            :disabled="true"
          ></Button>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - RadioGroup</template>
      <template #content>
        <div>
          <RadioGroup
            :options="['111', '222', '333', '444']"
            :initIndex="1"
            :disabledIndexs="[3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            :isRounded="false"
            :options="['111', '222', '333', '444']"
            :initIndex="1"
            :disabledIndexs="[3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            colorBackground="#ffffff"
            :colorForeground="blue"
            colorChangeMode="only-foreground"
            :options="['111', '222', '333', '444']"
            :initIndex="1"
            :disabledIndexs="[3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            colorBackground="#ffffff"
            :colorForeground="blue"
            colorChangeMode="only-foreground"
            :isRounded="false"
            :options="['111', '222', '333', '444']"
            :initIndex="1"
            :disabledIndexs="[3]"
            @change="console.log"
          ></RadioGroup>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - CheckboxGroup</template>
      <template #content>
        <div>
          <CheckboxGroup
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            :isRounded="true"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            colorBackground="#ffffff"
            :colorForeground="blue"
            colorChangeMode="only-foreground"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            :isRounded="true"
            colorBackground="#ffffff"
            :colorForeground="blue"
            colorChangeMode="only-foreground"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Switch</template>
      <template #content>
        <div>
          <Switch
            :init="true"
            color-bg-on="#ffffff"
            color-bg-off="#ffffff"
            @change="console.log"
          ></Switch>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Select</template>
      <template #content>
        <div style="height: 300px">
          <Select
            :options="['options1', 'options2', 'options33333333333']"
            :initIndex="2"
            @change="console.log"
          ></Select>
          <div style="width: 200px">
            <SelectAbsolute
              :options="[
                'options1',
                'options2',
                'options33333333333',
                'options4',
                'options5555',
                'options666666666666666666666666666666666666666666666666',
                'options7',
                'options8'
              ]"
              :initIndex="1"
              @change="console.log"
            ></SelectAbsolute>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - SlideLeftItem</template>
      <template #content>
        <div style="margin: 30px">
          <SlideLeftItem @delete="() => console.log('delete')">
            <div style="background-color: #ffffff">
              <Button>测试slide</Button>
            </div>
          </SlideLeftItem>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Input</template>
      <template #content>
        <div style="margin-top: 20px">
          <Input :validator="commonEmailValidator"></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholderType="underline"
            :showError="true"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            :validator="commonEmailValidator"
            defaultValue="489573239@qq.com"
            @input="console.log"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            placeholderType="jump"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            placeholderType="jump-each"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="width: 200px; margin-top: 20px; margin-bottom: 20px">
          <InputRange
            :defaultValue="20"
            :min="0"
            :max="200"
            :step="2"
          ></InputRange>
          <InputRangeBox
            :defaultValue="20"
            :min="0"
            :max="200"
            :step="2"
          ></InputRangeBox>
        </div>

        <div v-timestamp="`yyyy-mm-dd hh:ii:ss`">1695000491000</div>
        <div v-timestamp>{{ timestamp }}</div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Icon + SVG</template>
      <template #content>
        <div style="margin-top: 30px">
          <Status status="wait"></Status>
          <Status status="processing"></Status>
          <Status status="success"></Status>
          <Status status="error"></Status>
        </div>
        <div style="margin: 30px">
          <icon
            :icon="icon"
            :color="color"
            @click="iconClick"
          ></icon>
          <SVGPrompt></SVGPrompt>
          <SVGQuestion></SVGQuestion>
          <SVGFail></SVGFail>
          <SVGSuccess></SVGSuccess>
          <SVGWaiting></SVGWaiting>
          <SVGLock></SVGLock>
          <SVGDown></SVGDown>
          <div style="display: inline-block; width: 24px; height: 24px">
            <SVGAdd></SVGAdd>
          </div>
          <div style="display: inline-block; width: 24px; height: 24px">
            <SVGDelete></SVGDelete>
          </div>
          <div style="display: inline-block; width: 64px; height: 64px">
            <SVGWarn></SVGWarn>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Plate</template>
      <template #content>
        <div>
          <PlateDisplay
            plate="苏U888888"
            plateType="蓝牌"
            displayType="chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateType="蓝牌"
            displayType="dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateType="b"
            displayType="plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay plate="苏U666666"></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateType="b"
          ></PlateDisplay>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Pop</template>
      <template #content>
        <div>
          <div style="margin-top: 30px">
            <Button
              label="pop-bottom"
              @click="popBottomSample"
            ></Button>
            <Button
              label="pop-bottom-fixed"
              @click="popBottomFixedSample"
            ></Button>
            <Button
              label="pop-top"
              @click="popTopSample"
            ></Button>
            <Button
              label="pop-left"
              @click="popLeftSample"
            ></Button>
            <Button
              label="pop-right"
              @click="popRightSample"
            ></Button>
            <Button
              label="pop-center"
              @click="popCenterSample"
            ></Button>
            <Button
              label="pop-absolute"
              @click="popAbsoluteSample"
            ></Button>
            <Button
              label="pop-tel"
              @click="() => popTel('400-158-8363')"
            ></Button>
          </div>

          <div style="margin-top: 30px">
            <Popover>
              <template #panel>
                <div
                  style="width: 300px; height: 200px; background-color: white"
                >
                  Panel
                </div>
              </template>
            </Popover>
            <ColorPicker @color="console.log"></ColorPicker>
          </div>

          <!-- <div style="margin-top: 30px">
            <icon
              :icon="icon"
              :color="color"
              @click="click"
            ></icon>
            <SVGPrompt></SVGPrompt>
            <SVGQuestion></SVGQuestion>
            <SVGFail></SVGFail>
            <SVGSuccess></SVGSuccess>
            <SVGWaiting></SVGWaiting>
            <SVGLock></SVGLock>
            <SVGDown></SVGDown>
            <div style="display: inline-block; width: 24px; height: 24px">
              <SVGAdd></SVGAdd>
            </div>
            <div style="display: inline-block; width: 24px; height: 24px">
              <SVGDelete></SVGDelete>
            </div>
            <div style="display: inline-block; width: 64px; height: 64px">
              <SVGWarn></SVGWarn>
            </div>
          </div> -->
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Dialog</template>
      <template #content>
        <div>
          <Button
            label="dialog"
            @click="openDialogSample"
          ></Button>
          <Button
            label="dialog-loading"
            @click="openDialogLoadingSample"
          ></Button>
          <Button
            label="dialog-toast"
            @click="dialogToastSample"
          ></Button>
          <Button
            label="dialog-mini"
            @click="openDialogMiniSample"
          ></Button>
          <Button
            label="dialog-icon-warn-red"
            @click="openDialogRedSample"
          ></Button>
          <Button
            label="dialog-icon-warn-blue"
            @click="openDialogBlueSample"
          ></Button>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Keyboard</template>
      <template #content>
        <div>
          <Button
            label="NumberKeyboard"
            @click="openNumberKeyboardSample"
          ></Button>

          <PlateMobile @complete="console.log"></PlateMobile>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Image + ImageView</template>
      <template #content>
        <div style="width: 100%; height: 200px">
          <Image
            :src="imagesrc1"
            mode="aspectFill"
          ></Image>
        </div>
        <div>
          <Button
            label="open image view"
            @click="openImageViewMulti"
          ></Button>
          <ImageView
            ref="imageViewMultiRef"
            :urls="[imagesrc1, imagesrc2, imagesrc3, imagesrc2]"
          ></ImageView>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>手写板</template>
      <template #content>
        <div>
          <HandWritePanel ref="handRef"></HandWritePanel>
          <Button
            label="下载"
            @click="downloadHandWrite"
          ></Button>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>缓动函数 {{ easeName }}</template>
      <template #content>
        <div>
          <EaseGroupGraphDisplay
            @selectedEaseFunction="selectedEaseFunction"
          ></EaseGroupGraphDisplay>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>插值函数</template>
      <template #content>
        <div>
          <InterpolationGroupGraphDisplay></InterpolationGroupGraphDisplay>
        </div>
      </template>
    </Accordion>

    <Accordion
      ref="effectsRef"
      style="margin: 30px"
    >
      <template #title>CSSTransition + CSSAnimation + Effects</template>
      <template #content>
        <Mouse @enter="superRainbowEnter">
          <CSSTransition
            ref="superRainbowRef"
            name="superRainbow"
          >
            <div style="width: 100px; height: 100px">
              <Image
                :src="imagebike"
                @mount="imageMount"
              />
            </div>
          </CSSTransition>
        </Mouse>

        <div>
          <div
            v-for="(item, index) in [
            'jelly',
            'bounce',
            'flash',
            'pulse',
            'rubberBand',
            'shakeX',
            'shakeY',
            'headShake',
            'swing',
            'tada',
            'wobble',
            'jello',
            'heartBeat',
            'backInLeft',
            'backInRight',
            'backInUp',
            'backInDown',
            'backOutLeft',
            'backOutRight',
            'backOutUp',
            'backOutDown',
            'bounceIn',
            'bounceInLeft',
            'bounceInRight',
            'bounceInUp',
            'bounceInDown',
            'bounceOut',
            'bounceOutLeft',
            'bounceOutRight',
            'bounceOutUp',
            'bounceOutDown',
            'fadeIn',
            'fadeInLeft',
            'fadeInLeftBig',
            'fadeInRight',
            'fadeInRightBig',
            'fadeInUp',
            'fadeInUpBig',
            'fadeInDown',
            'fadeInDownBig',
            'fadeInTopLeft',
            'fadeInTopRight',
            'fadeInBottomLeft',
            'fadeInBottomRight',
            'fadeOut',
            'fadeOutLeft',
            'fadeOutLeftBig',
            'fadeOutRight',
            'fadeOutRightBig',
            'fadeOutUp',
            'fadeOutUpBig',
            'fadeOutDown',
            'fadeOutDownBig',
            'fadeOutTopLeft',
            'fadeOutTopRight',
            'fadeOutBottomRight',
            'fadeOutBottomLeft',
            'flip',
            'flipInX',
            'flipInY',
            'flipOutX',
            'flipOutY',
            'flipOutRight',
            'flipInLeft',
            'flipOutLeft',
            'flipInRight',
            'flipOutTop',
            'flipInBottom',
            'flipOutBottom',
            'flipInTop',
            'lightSpeedInLeft',
            'lightSpeedInRight',
            'lightSpeedOutLeft',
            'lightSpeedOutRight',
            'rotateIn',
            'rotateInUpLeft',
            'rotateInUpRight',
            'rotateInDownLeft',
            'rotateInDownRight',
            'rotateOut',
            'rotateOutUpLeft',
            'rotateOutUpRight',
            'rotateOutDownLeft',
            'rotateOutDownRight',
            'hinge',
            'rotateFall',
            'rotateOutNewspaper',
            'rotateInNewspaper',
            'jackInTheBox',
            'rollIn',
            'rollOut',
            'zoomIn',
            'zoomInLeft',
            'zoomInRight',
            'zoomInUp',
            'zoomInDown',
            'zoomOut',
            'zoomOutLeft',
            'zoomOutRight',
            'zoomOutUp',
            'zoomOutDown',
            'fade',
            'moveFromLeft',
            'moveToLeft',
            'moveFromRight',
            'moveToRight',
            'moveFromTop',
            'moveToTop',
            'moveFromBottom',
            'moveToBottom',
            'moveFromLeftFade',
            'moveToLeftFade',
            'moveFromRightFade',
            'moveToRightFade',
            'moveFromTopFade',
            'moveToTopFade',
            'moveFromBottomFade',
            'moveToBottomFade',
            'scaleFromSmall',
            'scaleToSmall',
            'scaleFromLarge',
            'scaleToLarge',
            'scaleFromSmallCenter',
            'scaleToSmallCenter',
            'slideInLeft',
            'slideInRight',
            'slideInUp',
            'slideInDown',
            'slideOutLeft',
            'slideOutRight',
            'slideOutUp',
            'slideOutDown',
            'rotateRightSideFirst',
            'rotateLeftSideFirst',
            'rotateTopSideFirst',
            'rotateBottomSideFirst',
            'rotatePushLeft',
            'rotatePushRight',
            'rotatePushTop',
            'rotatePushBottom',
            'rotatePullRight',
            'rotatePullLeft',
            'rotatePullTop',
            'rotatePullBottom',
            'rotateFoldRight',
            'rotateFoldLeft',
            'rotateFoldTop',
            'rotateFoldBottom',
            'rotateUnfoldLeft',
            'rotateUnfoldRight',
            'rotateUnfoldTop',
            'rotateUnfoldBottom',
            'rotateRoomLeftOut',
            'rotateRoomLeftIn',
            'rotateRoomRightOut',
            'rotateRoomRightIn',
            'rotateRoomTopOut',
            'rotateRoomTopIn',
            'rotateRoomBottomOut',
            'rotateRoomBottomIn',
            'rotateCubeLeftOut',
            'rotateCubeLeftIn',
            'rotateCubeRightOut',
            'rotateCubeRightIn',
            'rotateCubeTopOut',
            'rotateCubeTopIn',
            'rotateCubeBottomOut',
            'rotateCubeBottomIn',
            'rotateCarouselLeftOut',
            'rotateCarouselLeftIn',
            'rotateCarouselRightOut',
            'rotateCarouselRightIn',
            'rotateCarouselTopOut',
            'rotateCarouselTopIn',
            'rotateCarouselBottomOut',
            'rotateCarouselBottomIn',
            'rotateSidesOut',
            'rotateSidesIn',
            'rotateSlideOut',
            'rotateSlideIn',
            'statusProcessing'
            ] as SupportedCSSAnimation[]"
            :key="item"
            style="padding: 10px 0; border: 1px solid #cccccc"
          >
            <Mouse
              @enter="
                (e) => {
                  animationEnter(index);
                }
              "
              @leave="
                (e) => {
                  animationLeave(index);
                }
              "
            >
              <CSSAnimation
                :ref="(el) => CSSAnimationRefs.push(el)"
                :name="item"
              >
                CSSAnimation {{ item }}
              </CSSAnimation>
            </Mouse>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Page + Routes</template>
      <template #content>
        <div class="btns">
          <RadioGroup
            :options="[
              'static',
              'moveFromLeft',
              'moveToLeft',
              'moveFromRight',
              'moveToRight',
              'moveFromTop',
              'moveToTop',
              'moveFromBottom',
              'moveToBottom',
              'fade',
              'moveFromLeftFade',
              'moveToLeftFade',
              'moveFromRightFade',
              'moveToRightFade',
              'moveFromTopFade',
              'moveToTopFade',
              'moveFromBottomFade',
              'moveToBottomFade',
              'scaleFromSmall',
              'scaleToSmall',
              'scaleFromLarge',
              'scaleToLarge',
              'scaleFromSmallCenter',
              'scaleToSmallCenter',
              'rotateRightSideFirst',
              'rotateLeftSideFirst',
              'rotateTopSideFirst',
              'rotateBottomSideFirst',
              'flipOutRight',
              'flipInLeft',
              'flipOutLeft',
              'flipInRight',
              'flipOutTop',
              'flipInBottom',
              'flipOutBottom',
              'flipInTop',
              'rotateFall',
              'rotateOutNewspaper',
              'rotateInNewspaper',
              'rotatePushLeft',
              'rotatePushRight',
              'rotatePushTop',
              'rotatePushBottom',
              'rotatePullRight',
              'rotatePullLeft',
              'rotatePullTop',
              'rotatePullBottom',
              'rotateFoldRight',
              'rotateFoldLeft',
              'rotateFoldTop',
              'rotateFoldBottom',
              'rotateUnfoldLeft',
              'rotateUnfoldRight',
              'rotateUnfoldTop',
              'rotateUnfoldBottom',
              'rotateRoomLeftOut',
              'rotateRoomLeftIn',
              'rotateRoomRightOut',
              'rotateRoomRightIn',
              'rotateRoomTopOut',
              'rotateRoomTopIn',
              'rotateRoomBottomOut',
              'rotateRoomBottomIn',
              'rotateCubeLeftOut',
              'rotateCubeLeftIn',
              'rotateCubeRightOut',
              'rotateCubeRightIn',
              'rotateCubeTopOut',
              'rotateCubeTopIn',
              'rotateCubeBottomOut',
              'rotateCubeBottomIn',
              'rotateCarouselLeftOut',
              'rotateCarouselLeftIn',
              'rotateCarouselRightOut',
              'rotateCarouselRightIn',
              'rotateCarouselTopOut',
              'rotateCarouselTopIn',
              'rotateCarouselBottomOut',
              'rotateCarouselBottomIn',
              'rotateSidesOut',
              'rotateSidesIn',
              'rotateSlideOut',
              'rotateSlideIn'
            ]"
            @change="changePageEffect"
          ></RadioGroup>
        </div>
        <div>
          <Button @click="openPage">page: open</Button>
          <Button @click="closePage">page: close</Button>
        </div>
        <div
          style="
            width: 100%;
            height: 300px;
            border: 1px solid black;
            background-color: #333;
          "
        >
          <Page
            ref="pageRef"
            :effect="pageEffect"
          >
            <PlateMobile></PlateMobile>
          </Page>
        </div>
        <div style="padding: 30px 0; font-size: 16px">
          <div class="btns">
            <RadioGroup
              :options="[
                'moveToLeft',
                'moveToRight',
                'moveToTop',
                'moveToBottom',
                'moveFromLeftPrevFade',
                'moveFromRightPrevFade',
                'moveFromTopPrevFade',
                'moveFromBottomPrevFade',
                'moveToLeftFade',
                'moveToRightFade',
                'moveToTopFade',
                'moveToBottomFade',
                'moveToLeftEasing',
                'moveToRightEasing',
                'moveToTopEasing',
                'moveToBottomEasing',
                'moveFromLeftPrevScaleToSmall',
                'moveFromRightPrevScaleToSmall',
                'moveFromTopPrevScaleToSmall',
                'moveFromBottomPrevScaleToSmall',
                'scaleSmall',
                'scaleLarge',
                'scaleToLargePrevScaleToSmall',
                'scaleFromSmallPrevMoveToLeft',
                'scaleFromSmallPrevMoveToRight',
                'scaleFromSmallPrevMoveToTop',
                'scaleFromSmallPrevMoveToBottom',
                'glueToLeft',
                'glueToRight',
                'glueToTop',
                'glueToBottom',
                'flipToLeft',
                'flipToRight',
                'flipToTop',
                'flipToBottom',
                'rotateFall',
                'newspaper',
                'moveFromLeftPrevPush',
                'moveFromRightPrevPush',
                'moveFromTopPrevPush',
                'moveFromBottomPrevPush',
                'pullFromLeftPrevPush',
                'pullFromRightPrevPush',
                'pullFromTopPrevPush',
                'pullFromBottomPrevPush',
                'moveFromLeftFadePrevFold',
                'moveFromRightFadePrevFold',
                'moveFromTopFadePrevFold',
                'moveFromBottomFadePrevFold',
                'moveToLeftFadePrevUnfold',
                'moveToRightFadePrevUnfold',
                'moveToTopFadePrevUnfold',
                'moveToBottomFadePrevUnfold',
                'rotateRoomLeftIn',
                'rotateRoomRightIn',
                'rotateRoomTopIn',
                'rotateRoomBottomIn',
                'rotateCubeLeftIn',
                'rotateCubeRightIn',
                'rotateCubeTopIn',
                'rotateCubeBottomIn',
                'rotateCarouselLeftIn',
                'rotateCarouselRightIn',
                'rotateCarouselTopIn',
                'rotateCarouselBottomIn',
                'rotateSidesIn',
                'rotateSlideIn'
              ]"
              @change="changeRouteEffect"
            ></RadioGroup>
          </div>
          <div>
            <Button @click="pushPage">push</Button>
            <Button @click="popPage">pop</Button>
            <Button @click="gotoPage">goto0</Button>
            <Button @click="resetPage">reset</Button>
            <Button @click="backPage">back</Button>
            <Button @click="forwardPage">forward</Button>
          </div>
          <div
            style="
              width: 100%;
              height: 200px;
              border: 1px solid black;
              background-color: #333;
            "
          >
            <Routes
              ref="routesRef"
              :effect="routeEffect"
              @routeChangeStart="routeChangeStart"
              @routeChangeEnd="routeChangeEnd"
            />
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Video</template>
      <template #content>
        <div>
          <div>
            <Button @click="playVideo">play</Button>
            <Button @click="pauseVideo">pause</Button>
          </div>
          <Video
            ref="videoRef"
            :src="videosrc"
            :autoplay="false"
          ></Video>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>天地图</template>
      <template #content>
        <div style="height: 500px">
          <MapTianditu>
            <template v-slot="map">
              <div
                id="mapTiandituDiv"
                :style="{
                  width: map.width,
                  height: map.height
                }"
              ></div>
            </template>
          </MapTianditu>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Layout</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px">
          <div style="display: inline-block; width: 2rem; height: 4rem">
            <LayoutPage>
              <template #header>
                <div :style="{ height: '1rem', backgroundColor: '#5a82f0' }">
                  header
                </div>
              </template>
              <template #footer>
                <div :style="{ height: '1rem', backgroundColor: '#5a82f0' }">
                  footer
                </div>
              </template>
              <div>
                <div>main1</div>
                <div>main2</div>
                <div>main3</div>
                <div>main4</div>
                <div>main5</div>
                <div>main6</div>
                <div>main7</div>
                <div>main8</div>
                <div>main9</div>
                <div>main10</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main-last</div>
              </div>
            </LayoutPage>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>3D</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px; display: flex">
          <div style="width: 100px; height: 200px">
            <CardFlip>
              <template #front>
                <div style="background-color: #ff0000">front1</div>
              </template>
              <template #back>
                <div style="background-color: #00ff00">back1</div>
              </template>
            </CardFlip>
          </div>
          <div style="width: 100px; height: 200px">
            <CardFlip type="slide">
              <template #front>
                <div style="background-color: #0000ff">front2</div>
              </template>
              <template #back>
                <div style="background-color: #00ff00">back2</div>
              </template>
            </CardFlip>
          </div>
          <div>
            <Cube></Cube>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Loading</template>
      <template #content>
        <div
          style="padding: 30px 0; font-size: 16px"
          class="loadings"
        >
          <div>
            <Loading1></Loading1>
          </div>
          <div>
            <Loading2></Loading2>
          </div>
          <div>
            <Loading3></Loading3>
          </div>
          <div>
            <Loading4></Loading4>
          </div>
          <div>
            <Loading5></Loading5>
          </div>
          <div>
            <Loading6></Loading6>
          </div>
          <div>
            <Loading7></Loading7>
          </div>
          <div>
            <Loading8></Loading8>
          </div>
          <div>
            <Loading9></Loading9>
          </div>
          <div>
            <Loading10></Loading10>
          </div>
          <div>
            <Loading11></Loading11>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>ClockFlipper</template>
      <template #content>
        <Button
          label="start"
          @click="startClockFlipper"
        ></Button>
        <Button
          label="pause"
          @click="pauseClockFlipper"
        ></Button>
        <div style="width: 460px; height: 120px">
          <ClockFlipper ref="clockFlipper"></ClockFlipper>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Chat - RingText</template>
      <template #content>
        <div style="width: 200px; height: 200px">
          <RingText
            :text="'开启中...'"
            :secondText="'请您耐心等待'"
          ></RingText>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Chat - RingIndicator</template>
      <template #content>
        <Button
          label="reset"
          @click="resetRingIndicator"
        ></Button>
        <Button
          label="random"
          @click="addRingIndicator"
        ></Button>
        <div style="width: 400px; height: 200px">
          <RingIndicator :percent="percent"></RingIndicator>
        </div>
        <div style="width: 400px; height: 200px">
          <RingIndicator
            :half="true"
            :percent="percent"
          ></RingIndicator>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>CSS Effect</template>
      <template #content>
        <div style="margin: 30px">
          <Mouse
            @enter="() => cssMouseEnter('borderFocusTextRef')"
            @leave="() => cssMouseLeave('borderFocusTextRef')"
          >
            <BorderFocusText
              ref="borderFocusTextRef"
              :text="'当前时段'"
            >
              <div
                style="
                  width: 200px;
                  line-height: 50px;
                  background-color: #ffffff;
                "
              >
                something inner
              </div>
            </BorderFocusText>
          </Mouse>
          <Mouse
            @enter="() => cssMouseEnter('circleRef')"
            @leave="() => cssMouseLeave('circleRef')"
          >
            <Circle ref="circleRef">
              <div
                style="
                  width: 4em;
                  height: 4em;
                  line-height: 4em;
                  text-align: center;
                  background-color: pink;
                  border-radius: 100%;
                "
              >
                Circle
              </div>
            </Circle>
          </Mouse>
        </div>
      </template>
    </Accordion>
  </div>
</template>

<style scoped>
.loadings > div {
  display: inline-block;
  width: 100px;
  height: 100px;
  overflow: hidden;
  border: 1px solid green;
}
</style>
